import React from 'react'
import { Popover, List, ListItem, ListItemText, Divider } from '@mui/material'

export interface UserMenuItem {
    label: string
    href: string
}

interface UserMenuProps {
    open: boolean
    anchorEl: HTMLElement | null
    menuItems: UserMenuItem[]
    onClose: () => void
    onSignOut: () => void
    t: (key: string) => string
    variant: 'flyin' | 'cleartrip' | 'mobile'
    isRTL?: boolean
}

const UserMenu: React.FC<UserMenuProps> = ({ open, anchorEl, menuItems, onClose, onSignOut, t, variant, isRTL = false }) => {
    const mobileHorizontal = isRTL ? 'left' : 'right'
    
    const anchorOrigin = variant === 'mobile'
        ? { vertical: 'bottom' as const, horizontal: mobileHorizontal as 'left' | 'right' }
        : variant === 'flyin'
        ? { vertical: 'bottom' as const, horizontal: 'left' as const }
        : { vertical: 'bottom' as const, horizontal: 'right' as const }

    const transformOrigin = variant === 'mobile'
        ? { vertical: 'top' as const, horizontal: mobileHorizontal as 'left' | 'right' }
        : { vertical: 'top' as const, horizontal: 'right' as const }

    const paperProps = variant === 'cleartrip'
        ? {
              sx: {
                  mt: 1,
                  borderRadius: 1,
                  minWidth: 200,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'grey.200'
              }
          }
        : {
              sx: {
                  mt: 1,
                  borderRadius: variant === 'mobile' ? 1.5 : 1.5,
                  minWidth: variant === 'mobile' ? 185 : 185,
                  boxShadow: 3,
                  p: 0,
                  overflow: 'hidden',
                  background: '#fff'
              }
          }

    const listItemSx = variant === 'cleartrip'
        ? {
              py: 1.25,
              px: 2,
              color: 'text.secondary',
              fontSize: 14,
              '&:hover': {
                  bgcolor: 'grey.50',
                  color: 'primary.main'
              },
              transition: 'all 0.2s',
              textDecoration: 'none'
          }
        : {
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              py: 0.75,
              px: 2,
              minHeight: 40,
              border: 0,
              borderRadius: 0,
              fontWeight: 500,
              fontSize: 15,
              color: '#1a2a3a',
              background: '#fff',
              textDecoration: 'none',
              transition: 'background 0.2s',
              '&:hover': {
                  background: '#f3f4f6',
                  textDecoration: 'none'
              }
          }

    const signOutSx = variant === 'cleartrip'
        ? {
              py: 1.25,
              px: 2,
              color: 'text.secondary',
              fontSize: 14,
              cursor: 'pointer',
              '&:hover': {
                  bgcolor: 'grey.50',
                  color: '#FF6B35'
              },
              transition: 'all 0.2s'
          }
        : {
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              py: 0.5,
              px: 2,
              minHeight: 25,
              border: 0,
              borderRadius: 0,
              fontWeight: 500,
              fontSize: 15,
              color: 'error.main',
              background: '#fff',
              transition: 'background 0.2s',
              '&:hover': {
                  background: '#f3f4f6'
              }
          }

    const signOutTypographyProps = variant === 'cleartrip'
        ? { fontSize: 14, fontWeight: 400 }
        : { fontSize: 15, fontWeight: 500, color: 'error.main' }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            PaperProps={paperProps}
        >
            <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                {variant !== 'mobile' && menuItems.map((item, index) => (
                    <React.Fragment key={item.label}>
                        <ListItem
                            component="a"
                            href={item.href}
                            sx={listItemSx}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={
                                    variant === 'cleartrip'
                                        ? { fontSize: 14, fontWeight: 400 }
                                        : { fontSize: 15, fontWeight: 500, color: '#4b5563' }
                                }
                            />
                        </ListItem>
                        {variant === 'cleartrip' && index < menuItems.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
                {variant === 'cleartrip' && menuItems.length > 0 && <Divider />}
                <ListItem
                    component="button"
                    onClick={onSignOut}
                    sx={signOutSx}
                >
                    <ListItemText
                        primary={t('userMenu.signOut')}
                        primaryTypographyProps={signOutTypographyProps}
                    />
                </ListItem>
            </List>
        </Popover>
    )
}

export { UserMenu }
export type { UserMenuProps }
